import { Roboto_Condensed } from 'next/font/google';
import Layout from '../components/Layout';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Stack,
  Text,
  Textarea,
  useMediaQuery,
} from '@chakra-ui/react';
import Loader from '../components/Loader';
import { useEffect, useState } from 'react';
import { AiOutlinePercentage } from 'react-icons/ai';
import countryCodes from 'country-codes-list';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import Link from 'next/link';
import ChakraMarkdown from '../components/ChakraMarkdown';
import fetchJson from '../lib/fetchApi';
import { hasNonNullProperties, removeLeadingZero } from '../helpers';
import { useRouter } from 'next/router';
import Seo from '../components/Seo';

// Subsets are really important.
const roboto = Roboto_Condensed({
  weight: '400', // if single weight, otherwise you use array like [400, 500, 700],
  style: 'normal', // if single style, otherwise you use array like ['normal', 'italic']
  subsets: ['latin'],
});

type Affiliate = {
  first_name: string;
  last_name: string;
  country_code: Prefix;
  phone: string;
  email: string;
  instagram: string;
  x: string;
  linkedin: string;
  facebook: string;
  youtube: string;
  tiktok: string;
  other: string;
  industry: string;
  total_followers: string;
  average_engagement_rate: string;
  previous_collaborations: string;
  collaboration_expectations: string;
  collaboration_success: string;
  why_us: string;
  you_vs_us: string;
  policy_accepted: boolean;
};

type Prefix = {
  flag: string;
  countryNameEn: string;
  countryCallingCode: number;
};

const FOLLOWERS = [
  'Up to 5K',
  'Between 5K and 50K',
  'Between 50K and 500K',
  'Above 500K',
];
const INDUSTRY = ['travel', 'automotive', 'tech', 'fashion', 'fitness', 'food'];

const AffiliatePage = ({
  basicUser,
  basicPwdOne,
  basicPwdTwo,
  captchaSiteKey,
  baseUrl,
}) => {
  const [state, setState] = useState<Affiliate>({
    first_name: '',
    last_name: '',
    country_code: {
      flag: '🇬🇧',
      countryNameEn: 'United Kingdom',
      countryCallingCode: 44,
    },
    phone: '',
    email: '',
    instagram: '',
    x: '',
    linkedin: '',
    facebook: '',
    youtube: '',
    tiktok: '',
    other: '',
    industry: '',
    total_followers: '',
    average_engagement_rate: '',
    previous_collaborations: '',
    collaboration_expectations: '',
    collaboration_success: '',
    why_us: '',
    you_vs_us: '',
    policy_accepted: false,
  });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [validCaptcha, setValidCaptcha] = useState(true);
  const [errors, setErrors] = useState({
    first_name: null,
    last_name: null,
    country_code: null,
    phone: null,
    email: null,
    industry: null,
    total_followers: null,
    average_engagement_rate: null,
    previous_collaborations: null,
    collaboration_expectations: null,
    collaboration_success: null,
    why_us: null,
    you_vs_us: null,
  });

  const [isLargerThan728] = useMediaQuery('(min-width: 728px)');

  const stringToken = `${basicUser}:${basicPwdOne + basicPwdTwo}`;
  const token = Buffer.from(stringToken).toString('base64');

  const router = useRouter();

  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
    Authorization: `Basic ${token}`,
  };

  const TextHandler = (item: string) => {
    const words = item.split('_');
    const formattedStatus = words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return formattedStatus;
  };

  useEffect(() => {
    const country = countryCodes.all();
    const res = country.sort((a, b) =>
      a.countryNameEn.toLowerCase() < b.countryNameEn.toLowerCase()
        ? -1
        : a.countryNameEn.toLowerCase() > b.countryNameEn.toLowerCase()
        ? 1
        : 0
    );
    setCountries(res);
  }, []);

  const validateRecaptcha = (token) => {
    let status;
    try {
      axios
        .post(
          `${baseUrl}/client/validateRecaptcha`,
          {
            token: token,
          },
          { headers }
        )
        .then(({ data }) => {
          setValidCaptcha(data.success);

          status = data.success;
        });
    } catch (e) {
      console.log('Error', e);
    }
    return status;
  };

  const handleSubmit = async () => {
    const newErrors = { ...errors };

    if (state.first_name === '') {
      newErrors.first_name = 'First Name is required';
    } else {
      newErrors.first_name = null;
    }

    if (state.last_name === '') {
      newErrors.last_name = 'Last Name is required';
    } else {
      newErrors.last_name = null;
    }

    if (!state.country_code.countryCallingCode) {
      newErrors.country_code = 'Country Code is required';
    } else {
      newErrors.country_code = null;
    }

    if (state.phone === '') {
      newErrors.phone = 'Phone is required';
    } else {
      newErrors.phone = null;
    }

    if (state.email === '') {
      newErrors.email = 'Email is required';
    } else {
      newErrors.email = null;
    }

    if (state.industry === '') {
      newErrors.industry = 'Industry is required';
    } else {
      newErrors.industry = null;
    }

    if (state.total_followers === '') {
      newErrors.total_followers = 'Number of Followers is required';
    } else {
      newErrors.total_followers = null;
    }

    if (state.average_engagement_rate === '') {
      newErrors.average_engagement_rate = 'Average Engagement Rate is required';
    } else {
      newErrors.average_engagement_rate = null;
    }

    if (state.previous_collaborations === '') {
      newErrors.previous_collaborations = 'Previous Collaborations is required';
    } else {
      newErrors.previous_collaborations = null;
    }

    if (state.collaboration_expectations === '') {
      newErrors.collaboration_expectations = 'This field is required';
    } else {
      newErrors.collaboration_expectations = null;
    }

    if (state.collaboration_success === '') {
      newErrors.collaboration_success = 'This field is required';
    } else {
      newErrors.collaboration_success = null;
    }

    if (state.why_us === '') {
      newErrors.why_us = 'This field is required';
    } else {
      newErrors.why_us = null;
    }

    if (state.you_vs_us === '') {
      newErrors.you_vs_us = 'This field is required';
    } else {
      newErrors.you_vs_us = null;
    }

    // Update the errors state only once
    setErrors(newErrors);
    if (!hasNonNullProperties(newErrors)) {
      const data = await fetchJson(`/api/affiliate`, {
        method: 'POST',
        body: JSON.stringify({
          first_name: state.first_name,
          last_name: state.last_name,
          country_code: '+' + state.country_code.countryCallingCode,
          phone: removeLeadingZero(state.phone),
          email: state.email,
          instagram: state.instagram,
          x: state.x,
          linkedin: state.linkedin,
          facebook: state.facebook,
          youtube: state.youtube,
          tiktok: state.tiktok,
          other: state.other,
          industry: state.industry,
          total_followers: state.total_followers,
          average_engagement_rate: state.average_engagement_rate,
          previous_collaborations: state.previous_collaborations,
          collaboration_expectations: state.collaboration_expectations,
          collaboration_success: state.collaboration_success,
          why_us: state.why_us,
          you_vs_us: state.you_vs_us,
          policy_accepted: true,
        }),
      });

      router.reload();
    }
  };

  let seo = {};

  return (
    <Layout withAppNavBar={false}>
      <Seo seo={seo} />
      <Flex px={isLargerThan728 ? 20 : 10} py={20} direction={'column'}>
        <Flex direction={'column'}>
          <Stack
            spacing={3}
            w={isLargerThan728 ? 400 : '100%'}
            alignSelf={'center'}
          >
            <Text
              className={roboto.className}
              py={10}
              fontSize={'5xl'}
              textAlign={'left'}
            >
              Affiliate
            </Text>
            <Loader isLoading={loading} size={'xl'} />
            <Text textAlign={'left'}>About You</Text>
            <Input
              name="firstName"
              type="text"
              borderColor={!errors.first_name ? 'gray.200' : 'red.500'}
              placeholder="First Name"
              autoComplete="off"
              value={state.first_name}
              onChange={(e) => {
                setState({ ...state, first_name: e.target.value });
                setErrors({ ...errors, first_name: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.first_name && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.first_name}
              </Text>
            )}
            <Input
              name="lastName"
              type="text"
              borderColor={!errors.last_name ? 'gray.200' : 'red.500'}
              placeholder="Last Name"
              autoComplete="off"
              value={state.last_name}
              onChange={(e) => {
                setState({ ...state, last_name: e.target.value });
                setErrors({ ...errors, last_name: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.last_name && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.last_name}
              </Text>
            )}
            <Input
              name="email"
              type="email"
              borderColor={!errors.email ? 'gray.200' : 'red.500'}
              placeholder="Email"
              autoComplete="off"
              value={state.email}
              onChange={(e) => {
                setState({ ...state, email: e.target.value });
                setErrors({ ...errors, email: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.email && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.email}
              </Text>
            )}
            <Select
              name="country_code"
              borderColor={!errors.country_code ? 'gray.200' : 'red.500'}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              value={`${state.country_code.countryNameEn} +${state.country_code.countryCallingCode}`}
              onChange={(e) => {
                const parts = e.target.value.split(' ');
                const countryName = parts.slice(0, parts.length - 1).join(' ');
                const country_code = parts[parts.length - 1];

                setState({
                  ...state,
                  country_code: {
                    flag: countries.find(
                      (country) =>
                        country.countryCallingCode == country_code.substring(1)
                    ).flag,
                    countryNameEn: countryName,
                    countryCallingCode: Number(country_code),
                  },
                });

                setErrors({ ...errors, country_code: null });
              }}
              isRequired
            >
              {countries.map((country, index) => (
                <option
                  key={index}
                  value={`${country.countryNameEn} +${country.countryCallingCode}`}
                >
                  {country.flag} {country.countryNameEn} +
                  {country.countryCallingCode}
                </option>
              ))}
            </Select>
            {errors.country_code && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.country_code}
              </Text>
            )}
            <Input
              name="phone"
              type="text"
              onKeyDown={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              borderColor={!errors.phone ? 'gray.200' : 'red.500'}
              placeholder="Phone"
              autoComplete="off"
              value={state.phone}
              onChange={(e) => {
                setState({ ...state, phone: e.target.value });
                setErrors({ ...errors, phone: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.phone && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.phone}
              </Text>
            )}
            <Text textAlign={'left'}>Social Media</Text>
            <Input
              name="instagram"
              type="text"
              //   borderColor={validLastName ? 'gray.200' : 'red.500'}
              placeholder="Instagram"
              autoComplete="off"
              value={state.instagram}
              onChange={(e) =>
                setState({ ...state, instagram: e.target.value })
              }
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            <Input
              name="x"
              type="text"
              //   borderColor={validLastName ? 'gray.200' : 'red.500'}
              placeholder="X"
              autoComplete="off"
              value={state.x}
              onChange={(e) => setState({ ...state, x: e.target.value })}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            <Input
              name="linkedin"
              type="text"
              //   borderColor={validLastName ? 'gray.200' : 'red.500'}
              placeholder="Linkedin"
              autoComplete="off"
              value={state.linkedin}
              onChange={(e) => setState({ ...state, linkedin: e.target.value })}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            <Input
              name="facebook"
              type="text"
              //   borderColor={validLastName ? 'gray.200' : 'red.500'}
              placeholder="Facebook"
              autoComplete="off"
              value={state.facebook}
              onChange={(e) => setState({ ...state, facebook: e.target.value })}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            <Input
              name="youtube"
              type="text"
              //   borderColor={validLastName ? 'gray.200' : 'red.500'}
              placeholder="YouTube"
              autoComplete="off"
              value={state.youtube}
              onChange={(e) => setState({ ...state, youtube: e.target.value })}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            <Input
              name="tiktok"
              type="text"
              //   borderColor={validLastName ? 'gray.200' : 'red.500'}
              placeholder="TikTok"
              autoComplete="off"
              value={state.tiktok}
              onChange={(e) => setState({ ...state, tiktok: e.target.value })}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            <Input
              name="other"
              type="text"
              //   borderColor={validLastName ? 'gray.200' : 'red.500'}
              placeholder="Other"
              autoComplete="off"
              value={state.other}
              onChange={(e) => setState({ ...state, other: e.target.value })}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            <Select
              name="industry"
              borderColor={!errors.industry ? 'gray.200' : 'red.500'}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              placeholder="Select Industry"
              value={state.industry}
              onChange={(e) => {
                setState({ ...state, industry: e.target.value });
                setErrors({ ...errors, industry: null });
              }}
            >
              {INDUSTRY.map((item, index) => (
                <option key={index} value={item}>
                  {TextHandler(item)}
                </option>
              ))}
            </Select>
            {errors.industry && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.industry}
              </Text>
            )}
            <Select
              name="followers"
              borderColor={!errors.total_followers ? 'gray.200' : 'red.500'}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              placeholder="Select Number of Followers"
              value={state.total_followers}
              onChange={(e) => {
                setState({ ...state, total_followers: e.target.value });
                setErrors({ ...errors, total_followers: null });
              }}
            >
              {FOLLOWERS.map((item, index) => (
                <option key={index} value={item}>
                  {TextHandler(item)}
                </option>
              ))}
            </Select>
            {errors.total_followers && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.total_followers}
              </Text>
            )}
            <InputGroup>
              <Input
                name="aer"
                borderColor={
                  !errors.average_engagement_rate ? 'gray.200' : 'red.500'
                }
                placeholder="Average Engagement Rate"
                autoComplete="off"
                value={state.average_engagement_rate}
                onChange={(e) => {
                  setState({
                    ...state,
                    average_engagement_rate: e.target.value,
                  });
                  setErrors({ ...errors, average_engagement_rate: null });
                }}
                _focus={{
                  borderColor: 'brand.900',
                }}
                focusBorderColor="transparent"
                isRequired
              />
              <InputRightElement>
                <AiOutlinePercentage />
              </InputRightElement>
            </InputGroup>
            {errors.average_engagement_rate && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.average_engagement_rate}
              </Text>
            )}
            <Textarea
              name="previousCollaborations"
              borderColor={
                !errors.previous_collaborations ? 'gray.200' : 'red.500'
              }
              placeholder="Previous Collaborations"
              autoComplete="off"
              value={state.previous_collaborations}
              onChange={(e) => {
                setState({
                  ...state,
                  previous_collaborations: e.target.value,
                });
                setErrors({ ...errors, previous_collaborations: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.previous_collaborations && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.previous_collaborations}
              </Text>
            )}
            <Textarea
              name="collaborationExpectation"
              borderColor={
                !errors.collaboration_expectations ? 'gray.200' : 'red.500'
              }
              placeholder="What are your goals for this collaboration with Vert?"
              autoComplete="off"
              value={state.collaboration_expectations}
              onChange={(e) => {
                setState({
                  ...state,
                  collaboration_expectations: e.target.value,
                });
                setErrors({ ...errors, collaboration_expectations: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.collaboration_expectations && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.collaboration_expectations}
              </Text>
            )}
            <Textarea
              name="collacborationSuccess"
              borderColor={
                !errors.collaboration_success ? 'gray.200' : 'red.500'
              }
              placeholder="Do you have any specific ideas for content or campaigns we could create together?"
              autoComplete="off"
              value={state.collaboration_success}
              onChange={(e) => {
                setState({
                  ...state,
                  collaboration_success: e.target.value,
                });
                setErrors({ ...errors, collaboration_success: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.collaboration_success && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.collaboration_success}
              </Text>
            )}
            <Textarea
              name="whyUs"
              borderColor={!errors.why_us ? 'gray.200' : 'red.500'}
              placeholder="Why are you interested in collaborating with Vert?"
              autoComplete="off"
              value={state.why_us}
              onChange={(e) => {
                setState({
                  ...state,
                  why_us: e.target.value,
                });
                setErrors({ ...errors, why_us: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.why_us && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.why_us}
              </Text>
            )}
            <Textarea
              name="youVsUs"
              borderColor={!errors.you_vs_us ? 'gray.200' : 'red.500'}
              placeholder="How does your personal brand align with Vert's vision of luxury, elegance, and sustainable travel?"
              autoComplete="off"
              value={state.you_vs_us}
              onChange={(e) => {
                setState({
                  ...state,
                  you_vs_us: e.target.value,
                });
                setErrors({ ...errors, you_vs_us: null });
              }}
              _focus={{
                borderColor: 'brand.900',
              }}
              focusBorderColor="transparent"
              isRequired
            />
            {errors.you_vs_us && (
              <Text color="red.500" m={0} fontSize={12}>
                {errors.you_vs_us}
              </Text>
            )}
            <Box textAlign={'left'}>
              <ChakraMarkdown
                content={`<b>Promotion and Referral Platform:</b>

  We offer a unique referral programme to engage your followers and reward you. 
  You'll receive a custom referral code that your audience can use to download our 
  app

  - For every 10 people who download our app and sign up using your code, you'll earn "1 Eye," equivalent to £1, redeemable as cash.
  - Users who sign up with your code will receive "5 Eyes" to use towards their next ride.
    
  We see this programme as an innovative approach to advertising, where influencers play a pivotal role in expanding our brand's reach. We're excited to explore this with you as part of a long-term collaboration.
         
  <b>Additional Collaboration Opportunities:</b>

  Beyond our referral programme, our collaboration may include:     
            
  - <b>Social Media Features:</b> Showcase your experiences with Vert through posts, stories, and Reels on platforms like Instagram and TikTok.
  - <b>Content Creation:</b> Develop high-quality content, such as blog posts or YouTube videos, to highlight our luxury chauffeur services.
  - <b>Giveaways and Contests:</b> Engage your audience with exclusive promotions, contests, or discount codes.
  - <b>In-Person Experiences:</b> Attend Vert events, ride-along experiences, or promotional shoots for live or recorded content.
            `}
                onLinkClick={() => true}
              />
            </Box>
            <Text textAlign={'left'} fontSize={12}>
              By submitting this form, you confirm that you agree to our{' '}
              <Link
                href="/terms-and-conditions"
                style={{ textDecoration: 'underline', color: 'brand.900' }}
              >
                terms and conditions
              </Link>{' '}
              and{' '}
              <Link
                href="/privacy-policy"
                style={{ textDecoration: 'underline', color: 'brand.900' }}
              >
                privacy policy
              </Link>
              .
            </Text>
            <ReCAPTCHA
              sitekey={captchaSiteKey}
              onChange={(value) => {
                setCaptcha(value);
                validateRecaptcha(value);
              }}
              onExpired={() => setCaptcha('')}
            />
            {!validCaptcha && (
              <Text textAlign={'left'} fontSize={12} color={'red.500'}>
                Oops! It seems like you didn&apos;t enter the captcha correctly.
              </Text>
            )}
            <Button
              name="register"
              onClick={() => handleSubmit()}
              type="submit"
              backgroundColor={'brand.900'}
              shadow={'md'}
              color={'white'}
              _hover={{
                opacity: 0.7,
              }}
              fontWeight={'medium'}
              isDisabled={loading}
            >
              {loading ? 'Loading' : 'Register'}
            </Button>
          </Stack>
        </Flex>
      </Flex>
    </Layout>
  );
};

export default AffiliatePage;

export async function getServerSideProps() {
  return {
    props: {
      baseUrl: process.env.NEXT_APP_BASE_URL,
      basicUser: '$2b$10$' + process.env.NEXT_VERT_BASIC_USER,
      basicPwdOne: '$2b$10$' + process.env.NEXT_VERT_BASIC_PWD_PART_ONE,
      basicPwdTwo: process.env.NEXT_VERT_BASIC_PWD_PART_TWO,
      captchaSiteKey: process.env.RECAPTCHA_SITE_KEY,
    },
  };
}
